import { _notNil } from '@/littledash';
import type { FC } from 'react';
import { countOf, formatCount } from '@/components/Agents/SendValidator/utils/SendText';
import { aiNotesCount } from '@/components/Agents/SendValidator/utils/SendAiUse';
import { findingsNotShown, type SendOmission } from '@/components/Agents/SendValidator/utils/SendOmission';

interface SendOmissionNoticeProps {
  /** The checks' findings: how many the result shows, of how many were found. */
  findings?: Pick<SendOmission, 'shown' | 'found'>;
  /** How many Benchling In Vivo AI notes a limit left out. */
  aiNotesNotShown?: number;
  testId: string;
}

/**
 * Announces, with counts, the findings and Benchling In Vivo AI notes a limit left out of a SEND
 * result, so an omitted finding is never simply missing. Renders nothing when nothing was left out.
 */
const SendOmissionNotice: FC<SendOmissionNoticeProps> = ({ findings, aiNotesNotShown: aiNotesLeftOut = 0, testId }) => {
  const notShown = _notNil(findings) ? findingsNotShown(findings) : 0;
  const aiNotesNotShown = Math.max(0, aiNotesLeftOut);
  if (notShown === 0 && aiNotesNotShown === 0) {
    return null;
  }
  return (
    <div className="mt3 f6 dark-gray" data-test-component="SendOmissionNotice" data-testid={testId}>
      {notShown > 0 && (
        <p className="ma0" data-test-element="findings-not-shown" data-test-key={String(notShown)}>
          Showing {formatCount(findings?.shown ?? 0)} of {countOf(findings?.found ?? 0, 'finding', 'findings')} —{' '}
          {formatCount(notShown)} not shown. The report carries a limited number of examples; the counts include every
          finding.
        </p>
      )}
      {aiNotesNotShown > 0 && (
        <p className="ma0 mt1" data-test-element="ai-notes-not-shown" data-test-key={String(aiNotesNotShown)}>
          {aiNotesCount(aiNotesNotShown)} not shown — the checks&apos; own findings take priority when space is limited.
        </p>
      )}
    </div>
  );
};

export default SendOmissionNotice;
